import {
  memo, useCallback, useState
} from 'react';
import PropTypes from 'prop-types';
import SelectorListHidden from './SelectorListHidden';
import SelectorListVisible from './SelectorListVisible';
import LayoutWrapperHorizontal from '@/components/component-library/LayoutWrapperHorizontal';
import { classes } from '@/helpers/styling';

const OptionSelectorHorizontal = ({
  loading,
  options,
  onClickOption,
  selectedId,
  className,
  listClassName,
  optionClassName,
  OptionComponent,
  dropDownProps
}) => {
  const [optionsInView, setOptionsInView] = useState({});
  const [moreDropDownOpen, setMoreDropDownOpen] = useState(false);

  const handleOptionRender = useCallback(({ id: idOfOption, ref }) => {
    setOptionsInView((oldOptions) => ({
      ...oldOptions,
      [idOfOption]: {
        ref,
        inView: false
      }
    }));
  }, []);

  const handleOptionEnterView = useCallback(({ id: idOfOption, ref }) => {
    setOptionsInView((oldOptions) => ({
      ...oldOptions,
      [idOfOption]: {
        ref,
        inView: true
      }
    }));
  }, []);

  const handleOptionLeaveView = useCallback(({ id: idOfOption, ref }) => {
    setOptionsInView((oldOptions) => ({
      ...oldOptions,
      [idOfOption]: { ref }
    }));
  }, []);

  const handleOpenMoreDropDown = () => {
    setMoreDropDownOpen(true);
  };

  const handleCloseMoreOptions = () => {
    // Ensure that the closing of drop down happens after onBlur
    // This avoids the drop down failing to close when clicking on it
    setTimeout(() => setMoreDropDownOpen(false), 100);
  };

  const handleClickMoreOption = moreDropDownOpen
    ? handleCloseMoreOptions
    : handleOpenMoreDropDown;

  const handleSelectOption = (option) => {
    if (onClickOption) {
      onClickOption(option);
    }
  
    handleCloseMoreOptions();
  };

  const optionsCount = options.length;
  const optionsVisible = options.filter(({ id: optionId }) => !!(optionsInView[optionId] || {}).inView);
  const optionsVisibleCount = optionsVisible.length;
  const optionsInMoreDropDownCount = optionsCount - optionsVisibleCount;
  const shouldDisplayMoreDropDown = !loading && (optionsVisibleCount > 0) && optionsInMoreDropDownCount > 0;
  const optionIdsInMoreDropDown = shouldDisplayMoreDropDown
    ? options.slice(-(optionsInMoreDropDownCount + 1)).map(({ id: optionId }) => optionId)
    : options;
  const indexToRenderMoreDropDown = optionsVisibleCount - 1;
  const selectedOptionIsWithinMoreDropDown = optionIdsInMoreDropDown.includes(selectedId);
  const moreOptions = options.slice(indexToRenderMoreDropDown, options.length);

  // Calculate the max width of the "More" drop down options and use this as an adjustment for the rootMargin.
  // This ensures that we hide elements at the right time when this drop down is visible.
  // We could be more specific and choose the width of the selected drop down element,
  // but this can cause jerking when changing the drop down element itself to a different one and the size changes
  const rootMarginAdjustment = shouldDisplayMoreDropDown
    ? (
      moreOptions.reduce(
        (maxWidth, { id: optionId }) => {
          const elementWidth = optionsInView[optionId]?.ref?.current?.offsetWidth;

          return Math.max(maxWidth, elementWidth);
        },
        40 // Default adjustment for "More" option
      )
    ) : 0;

  return (
    <div className={ classes('selector-h', className) }>
      <LayoutWrapperHorizontal
        className='selector-h__list-wrapper'
        isRow
      >
        <SelectorListHidden
          className={ listClassName }
          onOptionEnterView={ handleOptionEnterView }
          onOptionLeaveView={ handleOptionLeaveView }
          onRenderOption={ handleOptionRender }
          optionClassName={ optionClassName }
          OptionComponent={ OptionComponent }
          options={ options }
          rootMarginAdjustment={ rootMarginAdjustment }
        />
        <SelectorListVisible
          className={ listClassName }
          dropDownProps={ dropDownProps }
          indexToDisplayMoreOption={ indexToRenderMoreDropDown }
          loading={ loading }
          moreOptionExpanded={ moreDropDownOpen }
          moreOptionHighlighted={ moreDropDownOpen || selectedOptionIsWithinMoreDropDown }
          moreOptions={ moreOptions }
          onClickMore={ handleClickMoreOption }
          onClickOption={ handleSelectOption }
          onCloseMore={ handleCloseMoreOptions }
          optionClassName={ optionClassName }
          OptionComponent={ OptionComponent }
          options={ optionsVisible }
          selectedId={ selectedId }
          shouldDisplayMoreOption={ shouldDisplayMoreDropDown }
        />
      </LayoutWrapperHorizontal>
    </div>
  );
};

OptionSelectorHorizontal.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ),
  className: PropTypes.string,
  listClassName: PropTypes.string,
  optionClassName: PropTypes.string,
  selectedId: PropTypes.string,
  onClickOption: PropTypes.func,
  loading: PropTypes.bool,
  OptionComponent: PropTypes.element,
  dropDownProps: PropTypes.object
};

OptionSelectorHorizontal.defaultProps = {
  options: [],
  className: '',
  listClassName: '',
  optionClassName: '',
  selectedId: undefined,
  onClickOption: undefined,
  loading: false,
  OptionComponent: undefined,
  dropDownProps: {}
};

export default memo(OptionSelectorHorizontal);